import { Controller } from '@hotwired/stimulus';

// Reloads turbo frames that nothing else knows have gone stale.
//
// A frame with a src fetches once and then sits there. That is what makes it
// useful for derived server state - counts, badges, metrics can load lazily,
// out of band, without the page waiting for them - but it also means they keep
// showing whatever was true when they first appeared. Whenever something else
// changes what they would say (a row gets toggled, a background job finishes,
// a record is created in a modal), somebody has to ask them to refetch.
//
// Reuse it for any such frame by passing the selector that matches it:
//
//   import { refreshFrames } from './refresh_frames_controller';
//   refreshFrames('turbo-frame[src*="/open_tickets_count/"]');
//
// or straight from markup, without writing JS, by putting this controller on
// whatever the server renders once the change has happened:
//
//   %div{ data: { controller: "refresh-frames",
//                 refresh_frames_selector_value: 'turbo-frame[src*="/open_tickets_count/"]' } }
//
// Debounced per selector, so a burst of callers costs one round of reloads:
// "Toggle All" fires one upload per row and each row is its own controller,
// yet a page of 50 devices must still refresh once.
const timers = new Map();

// Everything on the RBF claim screens that is derived from the claim's device
// claims: the metrics, the category badges and the show-page panels (devices
// list, payout breakdown, metadata). Shared, because every path that writes
// device claims - the collect table, the CSV import, the add-all job - leaves
// the same set of frames behind. A panel whose card is still collapsed has
// never loaded, and reload() leaves those lazy frames alone.
export const CLAIM_FRAMES =
  'turbo-frame[src*="/show_metric/"], turbo-frame[src*="/build_claim_pill/"], turbo-frame[src*="/show_panel?"]';

export function refreshFrames(selector, { delay = 400 } = {}) {
  clearTimeout(timers.get(selector));

  timers.set(
    selector,
    setTimeout(() => {
      timers.delete(selector);
      document.querySelectorAll(selector).forEach((frame) => frame.reload());
    }, delay)
  );
}

// Without a selector it refreshes the RBF claim frames, which is what most
// callers want (see rbf_claims/add_all_progress).
export default class extends Controller {
  static values = {
    selector: { type: String, default: CLAIM_FRAMES },
    delay: { type: Number, default: 0 },
  };

  connect() {
    refreshFrames(this.selectorValue, { delay: this.delayValue });
  }
}
